// Planning & Budgeting tab for the EPM module.
//
// Three vertically-stacked sections:
//   1. Scenario list — pick / create / clone / delete a plan
//   2. Drivers grid — table of driver × period, edit in place
//   3. Lines grid   — table of P&L lines with @driver / #account formulas
//                     + Compute button → output panel with totals
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Trash, GitBranch, Lock, LockOpen, Play } from "@phosphor-icons/react";

const INK    = "#0B1733";
const GOLD   = "#9B7B2E";
const INDIGO = "#0B1E3F";

// Tiny inline-edit input used in both grids.
const Cell = ({ value, onChange, type = "text", testid, placeholder = "" }) => (
  <input value={value ?? ""} type={type} placeholder={placeholder}
         onChange={(e) => onChange(type === "number" ? +e.target.value : e.target.value)}
         data-testid={testid}
         className="w-full bg-transparent border-b border-transparent focus:border-[#0B1E3F] focus:outline-none text-[12px] py-1 px-1.5 font-mono" />
);

const Empty = ({ children }) => (
  <div className="text-[12px] text-slate-500 italic">{children}</div>
);

// ---------------------------- Scenario list --------------------------------
const ScenarioList = ({ plans, activeId, onPick, onNew, onClone, onDelete }) => {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", fiscal_year: new Date().getFullYear(), currency: "USD" });
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5"
         data-testid="plan-scenario-list">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-bold tracking-tight" style={{ color: INK }}>Scenarios</h3>
        <span className="text-[11px] text-slate-500">Versioned plans · clone to branch</span>
        <button onClick={() => setCreating(!creating)} data-testid="plan-new-btn"
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider"
                style={{ color: INDIGO }}>
          <Plus size={12} /> {creating ? "Cancel" : "New scenario"}
        </button>
      </div>
      {creating && (
        <div className="grid grid-cols-4 gap-2 mb-4 p-3 border border-dashed rounded"
             style={{ borderColor: `${INDIGO}30` }}
             data-testid="plan-new-form">
          <input placeholder="Name (e.g. FY26 Budget)" value={draft.name}
                 onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                 className="border border-slate-300 px-2 py-1.5 text-sm" />
          <input type="number" placeholder="FY" value={draft.fiscal_year}
                 onChange={(e) => setDraft({ ...draft, fiscal_year: +e.target.value })}
                 className="border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder="Currency" value={draft.currency}
                 onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
                 maxLength={3} className="border border-slate-300 px-2 py-1.5 text-sm uppercase" />
          <button onClick={async () => {
            if (!draft.name || !draft.fiscal_year) return;
            await onNew(draft); setCreating(false); setDraft({ ...draft, name: "" });
          }}
                  data-testid="plan-create-btn"
                  className="bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">
            Create
          </button>
        </div>
      )}
      {plans.length === 0 ? (
        <Empty>No scenarios yet. Create your first plan to get started.</Empty>
      ) : (
        <ul className="space-y-1">
          {plans.map((p) => (
            <li key={p.id}
                className={`flex items-center gap-3 px-3 py-2 rounded cursor-pointer transition-colors ${
                  activeId === p.id ? "bg-[#0B1E3F]/5 border border-[#0B1E3F]/30" : "hover:bg-slate-50"
                }`}
                onClick={() => onPick(p.id)}
                data-testid={`plan-row-${p.id}`}>
              <div className="font-semibold text-sm" style={{ color: INK }}>{p.name}</div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm"
                    style={{ background: `${GOLD}1A`, color: GOLD }}>
                FY{p.fiscal_year} · v{p.version} · {p.currency}
              </span>
              {p.locked && <Lock size={12} color={GOLD} />}
              <span className="text-[10px] text-slate-500 capitalize">{p.status}</span>
              <button onClick={(e) => { e.stopPropagation(); onClone(p.id); }}
                      data-testid={`plan-clone-${p.id}`}
                      className="ml-auto text-[10px] text-[#0B1E3F] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                <GitBranch size={12} /> Clone
              </button>
              <button onClick={(e) => { e.stopPropagation();
                                          if (window.confirm(`Delete "${p.name}"?`)) onDelete(p.id); }}
                      className="text-slate-400 hover:text-red-600">
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------- Drivers grid ---------------------------------
const DriverGrid = ({ plan, drivers, onSave, onDelete }) => {
  const [draft, setDraft] = useState({ key: "", label: "", unit: "", values_by_period: {} });
  const periods = plan?.periods || [];
  const submit = async () => {
    if (!draft.key) return;
    await onSave(draft);
    setDraft({ key: "", label: "", unit: "", values_by_period: {} });
  };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="plan-drivers-grid">
      <h3 className="font-bold tracking-tight mb-2" style={{ color: INK }}>Drivers</h3>
      <p className="text-[11px] text-slate-500 mb-3">Reference these in line formulas as <code className="font-mono">@key</code>.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-2 py-2 font-bold">Key</th>
              <th className="text-left px-2 py-2 font-bold">Label</th>
              <th className="text-left px-2 py-2 font-bold">Unit</th>
              {periods.map((p) => (
                <th key={p} className="text-right px-2 py-2 font-bold font-mono">{p.slice(5)}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`plan-driver-row-${d.key}`}>
                <td className="px-2 py-1 font-mono font-semibold" style={{ color: GOLD }}>@{d.key}</td>
                <td className="px-2 py-1">{d.label}</td>
                <td className="px-2 py-1 text-slate-500">{d.unit}</td>
                {periods.map((p) => (
                  <td key={p} className="px-2 py-1 text-right">
                    <Cell type="number" value={d.values_by_period?.[p] ?? 0}
                          onChange={(v) => onSave({ ...d, values_by_period: { ...d.values_by_period, [p]: v } })}
                          testid={`plan-driver-${d.key}-${p}`} />
                  </td>
                ))}
                <td className="px-1">
                  <button onClick={() => onDelete(d.id)} className="text-slate-400 hover:text-red-600">
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {/* New driver row */}
            <tr className="border-t-2 border-[#0B1E3F]/15 bg-slate-50" data-testid="plan-driver-new-row">
              <td className="px-2 py-1">
                <Cell value={draft.key} onChange={(v) => setDraft({ ...draft, key: v.replace(/[^A-Za-z0-9_]/g, "") })}
                      placeholder="key" testid="plan-driver-new-key" />
              </td>
              <td className="px-2 py-1">
                <Cell value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} placeholder="Label" />
              </td>
              <td className="px-2 py-1">
                <Cell value={draft.unit} onChange={(v) => setDraft({ ...draft, unit: v })} placeholder="unit" />
              </td>
              <td colSpan={periods.length} className="px-2 py-1 text-[11px] text-slate-500 italic">
                Save first — then fill the per-period values inline.
              </td>
              <td className="px-1">
                <button onClick={submit} disabled={!draft.key} data-testid="plan-driver-save-btn"
                        className="text-[10px] font-bold uppercase tracking-wider text-[#0B1E3F] disabled:opacity-40">
                  + Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------- Lines + Output -------------------------------
const LinesAndOutput = ({ plan, lines, onSave, onDelete, onCompute, output }) => {
  const [draft, setDraft] = useState({ account: "", label: "", type: "income", formula: "", sort_order: 0 });
  const periods = plan?.periods || [];
  const submit = async () => {
    if (!draft.label) return;
    await onSave({ ...draft, sort_order: lines.length + 1 });
    setDraft({ account: "", label: "", type: "income", formula: "", sort_order: 0 });
  };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="plan-lines">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-bold tracking-tight" style={{ color: INK }}>P&amp;L lines</h3>
        <span className="text-[11px] text-slate-500">
          Use <code className="font-mono">@driver</code> + <code className="font-mono">#account</code> + arithmetic. Built-ins: min, max, abs, round.
        </span>
        <button onClick={onCompute} data-testid="plan-compute-btn"
                className="ml-auto bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
          <Play size={12} weight="fill" /> Compute
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-2 py-2 font-bold">Account</th>
              <th className="text-left px-2 py-2 font-bold">Label</th>
              <th className="text-left px-2 py-2 font-bold">Type</th>
              <th className="text-left px-2 py-2 font-bold">Formula</th>
              {periods.map((p) => (
                <th key={p} className="text-right px-2 py-2 font-bold font-mono">{p.slice(5)}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`plan-line-row-${l.id}`}>
                <td className="px-2 py-1 font-mono" style={{ color: GOLD }}>#{l.account}</td>
                <td className="px-2 py-1 font-semibold" style={{ color: INK }}>
                  <Cell value={l.label} onChange={(v) => onSave({ ...l, label: v })} />
                </td>
                <td className="px-2 py-1">
                  <select value={l.type} onChange={(e) => onSave({ ...l, type: e.target.value })}
                          className="bg-transparent text-[12px]">
                    <option value="income">income</option>
                    <option value="expense">expense</option>
                    <option value="memo">memo</option>
                  </select>
                </td>
                <td className="px-2 py-1 font-mono text-[11px]">
                  <Cell value={l.formula} onChange={(v) => onSave({ ...l, formula: v })}
                        testid={`plan-line-formula-${l.id}`} placeholder="e.g. @arpu * @cust" />
                </td>
                {periods.map((p) => (
                  <td key={p} className="px-2 py-1 text-right font-mono">
                    {(l.values_by_period?.[p] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                ))}
                <td className="px-1">
                  <button onClick={() => onDelete(l.id)} className="text-slate-400 hover:text-red-600">
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {/* New line row */}
            <tr className="border-t-2 border-[#0B1E3F]/15 bg-slate-50" data-testid="plan-line-new-row">
              <td className="px-2 py-1"><Cell value={draft.account} onChange={(v) => setDraft({ ...draft, account: v })} placeholder="acct#" testid="plan-line-new-account" /></td>
              <td className="px-2 py-1"><Cell value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} placeholder="Label" testid="plan-line-new-label" /></td>
              <td className="px-2 py-1">
                <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                        className="bg-transparent text-[12px]" data-testid="plan-line-new-type">
                  <option value="income">income</option>
                  <option value="expense">expense</option>
                  <option value="memo">memo</option>
                </select>
              </td>
              <td className="px-2 py-1 font-mono text-[11px]">
                <Cell value={draft.formula} onChange={(v) => setDraft({ ...draft, formula: v })} placeholder="formula" testid="plan-line-new-formula" />
              </td>
              <td colSpan={periods.length} />
              <td className="px-1">
                <button onClick={submit} disabled={!draft.label} data-testid="plan-line-save-btn"
                        className="text-[10px] font-bold uppercase tracking-wider text-[#0B1E3F] disabled:opacity-40">+ Add</button>
              </td>
            </tr>
            {/* Totals (computed) */}
            {output && output.totals && (
              <tr className="border-t-2 border-[#0B1E3F]/30 bg-[#0B1E3F]/5" data-testid="plan-totals-row">
                <td colSpan={4} className="px-2 py-2 font-bold uppercase tracking-wider text-[11px]" style={{ color: INK }}>
                  Net (Income − Expense)
                </td>
                {periods.map((p) => {
                  const v = output.totals[p] || 0;
                  return (
                    <td key={p} className={`px-2 py-2 text-right font-mono font-bold ${v >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                  );
                })}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {output?.errors?.length > 0 && (
        <div className="mt-3 text-[11px] text-amber-800" data-testid="plan-output-errors">
          {output.errors.length} formula error(s) — first: <span className="font-mono">{output.errors[0].error}</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------- Variance vs Actuals ---------------------------
const VariancePanel = ({ plan }) => {
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { api.get("/epm/ledgers").then((r) => setLedgers(r.data)); }, []);
  const run = async () => {
    if (!ledgerId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/epm/plans/${plan.id}/variance`,
                                       { params: { ledger_id: ledgerId } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  const periods = plan?.periods || [];
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="plan-variance">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-bold tracking-tight" style={{ color: INK }}>Plan vs Actuals</h3>
        <span className="text-[11px] text-slate-500">
          Joins this plan&apos;s line accounts to Trial Balance lines for the same periods.
        </span>
        <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)}
                data-testid="variance-ledger"
                className="ml-auto border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="">— pick a ledger —</option>
          {ledgers.map((l) => (
            <option key={l.id} value={l.id}>{l.name} · {l.currency}</option>
          ))}
        </select>
        <button onClick={run} disabled={!ledgerId || loading} data-testid="variance-run-btn"
                className="bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">
          {loading ? "Loading…" : "Compare"}
        </button>
      </div>
      {error && <div className="text-[11px] text-red-700" data-testid="variance-error">{error}</div>}
      {data && (
        <>
          {data.periods_with_actuals.length === 0 && (
            <div className="text-[11px] text-amber-700 mb-3" data-testid="variance-empty">
              No Trial Balance found for this ledger across the plan&apos;s periods.
              Pull a TB on the Trial Balances tab first.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-2 py-2 font-bold">Account</th>
                  <th className="text-left px-2 py-2 font-bold">Label</th>
                  <th className="text-left px-2 py-2 font-bold">Row</th>
                  {periods.map((p) => (
                    <th key={p} className="text-right px-2 py-2 font-bold font-mono">{p.slice(5)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.lines.map((ln) => (
                  <React.Fragment key={ln.line_id}>
                    <tr className="border-t-2 border-slate-200" data-testid={`var-row-plan-${ln.line_id}`}>
                      <td rowSpan={3} className="px-2 py-1 font-mono font-semibold" style={{ color: GOLD }}>#{ln.account}</td>
                      <td rowSpan={3} className="px-2 py-1 font-semibold" style={{ color: INK }}>{ln.label}</td>
                      <td className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Plan</td>
                      {periods.map((p) => (
                        <td key={p} className="px-2 py-1 text-right font-mono">
                          {(ln.plan_by_period[p] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                      ))}
                    </tr>
                    <tr data-testid={`var-row-actual-${ln.line_id}`}>
                      <td className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: INDIGO }}>Actual</td>
                      {periods.map((p) => (
                        <td key={p} className="px-2 py-1 text-right font-mono">
                          {(ln.actual_by_period[p] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-slate-50/60" data-testid={`var-row-pct-${ln.line_id}`}>
                      <td className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Var %</td>
                      {periods.map((p) => {
                        const pct = ln.variance_pct_by_period[p];
                        if (pct === null || pct === undefined) {
                          return <td key={p} className="px-2 py-1 text-right text-slate-400 font-mono">—</td>;
                        }
                        const tone = Math.abs(pct) < 5 ? "text-slate-600"
                                   : pct >= 0 ? "text-emerald-700" : "text-red-700";
                        return (
                          <td key={p} className={`px-2 py-1 text-right font-mono font-semibold ${tone}`}>
                            {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------- Top-level tab --------------------------------
const PlanTab = () => {
  const [plans, setPlans] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [detail, setDetail] = useState({ plan: null, drivers: [], lines: [] });
  const [output, setOutput] = useState(null);

  const loadList = async () => {
    const { data } = await api.get("/epm/plans");
    setPlans(data);
    if (data.length > 0 && !activeId) setActiveId(data[0].id);
  };
  const loadDetail = async (pid) => {
    if (!pid) return setDetail({ plan: null, drivers: [], lines: [] });
    const { data } = await api.get(`/epm/plans/${pid}`);
    setDetail(data);
    setOutput(null);
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { loadDetail(activeId); }, [activeId]);

  const create = async (body) => {
    const { data } = await api.post("/epm/plans", body);
    await loadList();
    setActiveId(data.id);
  };
  const clone = async (pid) => {
    const { data } = await api.post(`/epm/plans/${pid}/clone`, {});
    await loadList();
    setActiveId(data.id);
  };
  const remove = async (pid) => {
    await api.delete(`/epm/plans/${pid}`);
    setActiveId("");
    await loadList();
  };
  const saveDriver = async (d) => {
    await api.post(`/epm/plans/${activeId}/drivers`, d);
    await loadDetail(activeId);
  };
  const removeDriver = async (id) => {
    await api.delete(`/epm/plans/${activeId}/drivers/${id}`);
    await loadDetail(activeId);
  };
  const saveLine = async (l) => {
    await api.post(`/epm/plans/${activeId}/lines`, l);
    await loadDetail(activeId);
  };
  const removeLine = async (id) => {
    await api.delete(`/epm/plans/${activeId}/lines/${id}`);
    await loadDetail(activeId);
  };
  const compute = async () => {
    try {
      const { data } = await api.post(`/epm/plans/${activeId}/compute`);
      setOutput(data);
      await loadDetail(activeId);
    } catch (e) {
      setOutput({ errors: [{ error: e?.response?.data?.detail || e.message }] });
    }
  };

  return (
    <div className="space-y-6" data-testid="plan-tab">
      <ScenarioList plans={plans} activeId={activeId}
                     onPick={setActiveId} onNew={create} onClone={clone} onDelete={remove} />
      {detail.plan ? (
        <>
          <DriverGrid plan={detail.plan} drivers={detail.drivers}
                       onSave={saveDriver} onDelete={removeDriver} />
          <LinesAndOutput plan={detail.plan} lines={detail.lines}
                           onSave={saveLine} onDelete={removeLine}
                           onCompute={compute} output={output} />
          <VariancePanel plan={detail.plan} />
        </>
      ) : (
        <Empty>Pick a scenario above (or create a new one) to start planning.</Empty>
      )}
    </div>
  );
};

export default PlanTab;
